import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@/analytics';
import API from '@/api';
import { queryKeys } from '@/api/utils/queryKeys';
import Send2Icon from '@/components/icons/Send2Icon';
import type { ContactVerificationSuccessPayload } from '@/components/ModalRenderer/components/SE/contactVerificationSuccess';
import useOnContactVerificationSuccess from '@/components/ModalRenderer/components/SE/useOnContactVerificationSuccess';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import useCountdown from '@/components/OTPVerification/hooks/useCountdown';
import OTPForm from '@/components/OTPVerification/OTPForm/OTPForm';
import { AnalyticsEvents } from '@/constants/analytics';
import { ErrorIds } from '@/constants/ApiResponse';
import useModal from '@/hooks/useModal/useModal';
import useRequestEmailVerificationCode from '@/hooks/useRequestEmailVerificationCode';
import { formatElapsedDuration } from '@/localization/duration/duration';
import Button from '@/ui-kit/components/Button/Button';
import { parseApiError } from '@/utils/error';

type Payload = ContactVerificationSuccessPayload & { email: string; resendAfter: string };

const EmailVerificationOtp = ({
  payload: { email, resendAfter, successAction },
  closeModal,
}: ModalProps<Payload>) => {
  const { t } = useTranslation(['Modal', 'FormErrors']);
  const { openModal } = useModal();
  const handleSuccessAction = useOnContactVerificationSuccess(successAction);
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState(0);
  const [resendExpiration, setResendExpiration] = useState(() => new Date(resendAfter));
  const [emailSent, setEmailSent] = useState(false);

  const resetFormFields = useCallback(() => {
    setFormKey(prevKey => prevKey + 1);
  }, []);

  const {
    mutate: verifyCode,
    isPending: isVerifyPending,
    error: verifyError,
    reset: resetVerifyMutation,
  } = useMutation({
    mutationFn: API.user.verifyEmailVerificationCode,
    onSuccess: async () => {
      // creditActions may have no active observer on the profile path; refetchType:'all'
      // ensures the cache is fresh before the success modal opens and reads it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({ queryKey: queryKeys.creditActions, refetchType: 'all' }),
      ]);
      openModal('SE.EMAIL_VERIFICATION_SUCCESS', { payload: { successAction } });
    },
    onError: resetFormFields,
  });

  const {
    requestEmailVerificationCode: resendCode,
    isPending: isResendPending,
    errorMessage: resendErrorMessage,
    reset: resetResendMutation,
  } = useRequestEmailVerificationCode({
    onSuccess: ({ resendAfter: nextResendAfter }) => {
      setResendExpiration(new Date(nextResendAfter));
      resetFormFields();
      resetVerifyMutation();
      setEmailSent(true);
    },
    onAlreadyVerified: () => {
      closeModal();
      handleSuccessAction();
    },
  });

  const handleVerify = useCallback(
    (verificationCode: string) => {
      resetVerifyMutation();
      resetResendMutation();
      setEmailSent(false);
      verifyCode(verificationCode);
    },
    [resetVerifyMutation, resetResendMutation, verifyCode],
  );

  const handleCodeChange = useCallback(
    (newCode: string) => {
      if (newCode.length === 0) {
        return;
      }

      setEmailSent(false);
      // Only reset when an error is showing — unconditional reset re-renders forever
      // because OTPForm's onCodeChange effect depends on this callback identity.
      if (verifyError) {
        resetVerifyMutation();
      }
    },
    [verifyError, resetVerifyMutation],
  );

  const resendCooldownSeconds = useCountdown(resendExpiration);
  const isResendDisabled = resendCooldownSeconds > 0 || isResendPending || isVerifyPending;

  const { errorId, metadata } = verifyError ? parseApiError(verifyError) : {};
  const triesLeft = metadata?.codeTriesLeft;
  const isWrongCodeWithTries = errorId === ErrorIds.WRONG_CODE && typeof triesLeft === 'number';
  const isTerminalVerifyError =
    errorId === ErrorIds.MAX_VERIFICATION_TRIES_REACHED || errorId === ErrorIds.CODE_EXPIRED;
  const canEnterCode = !isTerminalVerifyError;
  const isInvalid = isWrongCodeWithTries || isTerminalVerifyError;

  let inlineErrorMessage: string | undefined;
  if (resendErrorMessage) {
    inlineErrorMessage = resendErrorMessage;
  } else if (verifyError) {
    if (isWrongCodeWithTries) {
      inlineErrorMessage = t('Modal:EmailVerificationOtp.wrongCode', { count: triesLeft });
    } else if (errorId === ErrorIds.CODE_EXPIRED) {
      inlineErrorMessage = t('Modal:EmailVerificationOtp.codeExpired');
    } else if (errorId === ErrorIds.MAX_VERIFICATION_TRIES_REACHED) {
      inlineErrorMessage = t('Modal:EmailVerificationOtp.maxTriesReached');
    } else {
      inlineErrorMessage = t('FormErrors:somethingWentWrong');
    }
  }

  const resendLabel =
    resendCooldownSeconds > 0
      ? t('Modal:EmailVerificationOtp.resendWithCountdown', {
          duration: formatElapsedDuration(resendCooldownSeconds),
        })
      : t('Modal:EmailVerificationOtp.resend');

  return (
    <Modal title="">
      <div className="-mt-2 mb-6 flex flex-col items-center text-center">
        <Send2Icon className="text-icon-brand mb-4 size-12" aria-hidden="true" />
        <h2 className="text-heading mb-2 lg:text-center">{t('Modal:EmailVerificationOtp.title')}</h2>
        <div className="text-text-subtle flex flex-col gap-1">
          <p>{t('Modal:EmailVerificationOtp.subtitle')}</p>
          <span data-private>{email}</span>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <OTPForm
          key={formKey}
          onSubmit={handleVerify}
          onCodeChange={handleCodeChange}
          isInvalid={isInvalid}
          isDisabled={!canEnterCode || isVerifyPending || isResendPending}
          errorMessage={inlineErrorMessage}
          infoMessage={
            emailSent && !inlineErrorMessage ? t('Modal:EmailVerificationOtp.codeSent') : undefined
          }
        />
        <ModalActions>
          <Button
            className="tabular-nums"
            type="button"
            variant="tertiary"
            disabled={isResendDisabled}
            loading={isResendPending}
            onClick={() => {
              trackEvent(AnalyticsEvents.SE.EMAIL_VERIFICATION_OTP_RESEND);
              setEmailSent(false);
              resendCode(email);
            }}
          >
            {resendLabel}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
};

export default EmailVerificationOtp;
